import { isDesktop } from '../lib/platform.ts'

/**
 * Ponte com a JANELA do Orbind Desktop (SPEC-20261104-0202-barra-da-janela-do-desktop).
 *
 * A partir do Desktop 0.3.8 a janela nasce SEM a decoração nativa do Windows
 * e a barra de título é desenhada pelo próprio app (`BarraDaJanela`), como o
 * Claude Desktop faz: dá para pôr ícones nela e ela segue o tema. Esta ponte
 * fala com o plugin `window` do Tauri 2 por `__TAURI_INTERNALS__`, sem
 * `@tauri-apps/api` (o mesmo bundle roda no navegador — ver `desktop.ts`).
 *
 * Builds antigos (≤ 0.3.7) não têm o comando `janela_info`: a chamada falha,
 * `semDecoracao()` responde false e a barra não aparece — senão ficariam duas
 * barras, a nativa e a nossa.
 */

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>
type Internals = { invoke?: Invoke; metadata?: { currentWindow?: { label?: string } } }

function internos(): Internals | null {
  if (!isDesktop()) return null
  return (window as unknown as { __TAURI_INTERNALS__?: Internals }).__TAURI_INTERNALS__ ?? null
}

/** Rótulo da janela atual (main, extra-1…) — o plugin exige dizer qual janela. */
function rotulo(): string {
  return internos()?.metadata?.currentWindow?.label ?? 'main'
}

async function janela(comando: string, extra?: Record<string, unknown>): Promise<unknown> {
  const invoke = internos()?.invoke
  if (!invoke) return null
  return invoke(`plugin:window|${comando}`, { label: rotulo(), ...extra })
}

export const janelaDesktop = {
  /** A janela está sem a decoração nativa (precisa da nossa barra)? */
  semDecoracao: async (): Promise<boolean> => {
    const invoke = internos()?.invoke
    if (!invoke) return false
    try {
      const r = (await invoke('janela_info')) as { semDecoracao?: boolean } | null
      return r?.semDecoracao === true
    } catch {
      return false
    }
  },
  minimizar: () => janela('minimize'),
  alternarMaximizar: () => janela('toggle_maximize'),
  fechar: () => janela('close'),
  estaMaximizada: async (): Promise<boolean> => {
    try {
      return (await janela('is_maximized')) === true
    } catch {
      return false
    }
  },
  /** Começa a arrastar a janela — chamado no mousedown da área livre da barra. */
  arrastar: () => janela('start_dragging'),
}
